import { getCurrentScope, onScopeDispose, reactive, ref, watch } from 'vue';

const publishedModes = new Set(['active', 'silent']);
const isPublishedMode = mode =>
  publishedModes.has(String(mode || '').toLowerCase());

export const useAgentPublicationLifecycle = ({
  assistantId,
  identityForm,
  lifecycleStatus,
  validate,
  buildSettings,
  saveSettings,
  dispatch,
  markSaved,
  fetchVersions,
  reviewIssue,
  readPreflightError,
  notify,
  track,
  trackEvent,
  t,
}) => {
  const isSaving = ref(false);
  const showUnpublishConfirmation = ref(false);
  const preflight = reactive({ blockers: [], warnings: [] });
  let requestId = 0;

  const clearPreflight = () => {
    preflight.blockers = [];
    preflight.warnings = [];
  };

  const isCurrentRequest = (currentRequestId, targetAssistantId) =>
    currentRequestId === requestId && targetAssistantId === assistantId.value;

  const invalidate = () => {
    requestId += 1;
    isSaving.value = false;
    showUnpublishConfirmation.value = false;
    clearPreflight();
  };

  const isCurrentlyPublished = () => isPublishedMode(lifecycleStatus.value);

  const save = async ({ confirmedUnpublish = false } = {}) => {
    if (!validate()) return false;

    const selectedMode = identityForm.publicationMode;
    const wasPublished = isCurrentlyPublished();
    if (selectedMode === 'draft' && wasPublished && !confirmedUnpublish) {
      showUnpublishConfirmation.value = true;
      return false;
    }

    clearPreflight();
    const targetAssistantId = assistantId.value;
    requestId += 1;
    const currentRequestId = requestId;
    const isPublishing = isPublishedMode(selectedMode);
    if (!isPublishing) identityForm.rolloutPercent = 0;
    isSaving.value = true;

    try {
      const saved = await saveSettings({
        config: buildSettings(),
        targetAssistantId,
      });
      if (!saved || !isCurrentRequest(currentRequestId, targetAssistantId)) {
        return false;
      }

      // The PATCH persists draft settings independently from lifecycle. This
      // baseline must remain updated even when publish preflight later blocks.
      markSaved(['publication']);

      let lifecycleResponse;
      if (isPublishing) {
        lifecycleResponse = await dispatch('captainAssistants/publish', {
          assistantId: targetAssistantId,
          publicationMode: selectedMode,
          leadRolloutPercent: Number(identityForm.rolloutPercent || 0),
        });
      } else if (wasPublished) {
        lifecycleResponse = await dispatch('captainAssistants/unpublish', {
          assistantId: targetAssistantId,
        });
      }

      if (!isCurrentRequest(currentRequestId, targetAssistantId)) return false;

      // Lifecycle is a separate baseline: only acknowledge it once the
      // lifecycle command has finished (or when no command was necessary).
      markSaved(['publicationLifecycle']);
      const warnings =
        lifecycleResponse?.agent_configuration?.readiness?.warnings;
      preflight.warnings = Array.isArray(warnings) ? warnings : [];

      await fetchVersions();
      if (!isCurrentRequest(currentRequestId, targetAssistantId)) return false;

      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_PUBLISH_UPDATED'));
      track(trackEvent, {
        assistantId: targetAssistantId,
        qualificationMode: identityForm.qualificationMode,
        operationScope: identityForm.operationScope,
        publicationMode: selectedMode,
        rolloutPercent: Number(identityForm.rolloutPercent || 0),
        allowedChannelCount: identityForm.allowedChannels.length,
        directWhatsappBehavior: identityForm.directWhatsappBehavior,
        websiteDirectLinkPolicy: identityForm.websiteDirectLinkPolicy,
        outOfHoursBehavior: identityForm.outOfHoursBehavior,
      });
      return true;
    } catch (error) {
      if (!isCurrentRequest(currentRequestId, targetAssistantId)) return false;

      const parsedPreflight = readPreflightError(error);
      if (parsedPreflight) {
        preflight.blockers = parsedPreflight.blockers;
        preflight.warnings = parsedPreflight.warnings;
        const firstBlocker = parsedPreflight.blockers[0];
        notify(
          firstBlocker?.message ||
            t('CAPTAIN_EXPERIENCE.WORKSPACE.PREFLIGHT_BLOCKERS_DESCRIPTION')
        );
        if (firstBlocker) await reviewIssue(firstBlocker);
        return false;
      }

      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_PUBLISH_ERROR')
      );
      return false;
    } finally {
      if (isCurrentRequest(currentRequestId, targetAssistantId)) {
        isSaving.value = false;
      }
    }
  };

  const requestSave = () => {
    if (identityForm.publicationMode === 'draft' && isCurrentlyPublished()) {
      showUnpublishConfirmation.value = true;
      return false;
    }

    return save();
  };

  const confirmUnpublish = () => {
    showUnpublishConfirmation.value = false;
    return save({ confirmedUnpublish: true });
  };

  const cancelUnpublish = () => {
    if (!isSaving.value) showUnpublishConfirmation.value = false;
  };

  const stopAssistantWatch = watch(assistantId, invalidate);
  const dispose = () => {
    stopAssistantWatch();
    invalidate();
  };
  if (getCurrentScope()) onScopeDispose(dispose);

  return {
    isSaving,
    showUnpublishConfirmation,
    preflight,
    clearPreflight,
    save,
    requestSave,
    confirmUnpublish,
    cancelUnpublish,
    dispose,
  };
};
